import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { checkSharedLinkExists } from './check-shared-link-exists';
import { getSharedLink, getSharedLinkPublic } from './get-shared-link';
import type { SharedLinkTargetType } from './types';

export const sharedLinkQueryKeys = {
  all: ['shared-link'] as const,
  detail: (linkId: string) =>
    [...sharedLinkQueryKeys.all, 'detail', linkId] as const,
  targetDetail: (
    workspaceId: string,
    targetType: SharedLinkTargetType,
    targetId: string,
  ) =>
    [
      ...sharedLinkQueryKeys.all,
      'target-detail',
      workspaceId,
      targetType,
      targetId,
    ] as const,
  public: (linkId: string) =>
    [...sharedLinkQueryKeys.all, 'public', linkId] as const,
};

export function sharedLinkByTargetQueryOptions({
  workspaceId,
  targetId,
  targetType,
  enabled = true,
}: {
  workspaceId: string;
  targetId: string;
  targetType: SharedLinkTargetType;
  enabled?: boolean;
}) {
  return queryOptions({
    queryKey: sharedLinkQueryKeys.targetDetail(
      workspaceId,
      targetType,
      targetId,
    ),
    queryFn: () =>
      checkSharedLinkExists({
        workspaceId,
        targetId,
        targetType,
      }),
    enabled: Boolean(workspaceId && targetId) && enabled,
    retry: false,
  });
}

export const useSharedLinkQuery = (linkId: string) => {
  return useSuspenseQuery({
    queryKey: sharedLinkQueryKeys.detail(linkId),
    queryFn: () => getSharedLink({ linkId }),
  });
};

export const useSharedLinkPublicQuery = (
  linkId: string,
  password?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: sharedLinkQueryKeys.public(linkId),
    queryFn: () => getSharedLinkPublic({ linkId, password }),
    enabled: !!linkId && options?.enabled !== false,
  });
};
